import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Target, Check, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function DailyChallenge() {
  const { refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/challenges/today");
      setState(data);
    } catch (err) {
      // Silently ignore — challenges are a nice-to-have
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const claim = async () => {
    if (!state?.completed || state.claimed) return;
    setLoading(true);
    try {
      const { data } = await api.post("/challenges/claim");
      toast.success(`+${data.bc_earned} BC — ${state.challenge.title}`);
      await load();
      if (typeof refreshUser === "function") await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Claim failed");
    } finally {
      setLoading(false);
    }
  };

  if (!state) return null;

  const pct = Math.min(100, Math.round((state.progress / state.goal) * 100));
  const { challenge, completed, claimed, progress, goal } = state;

  let buttonLabel = `${progress} / ${goal}`;
  if (claimed) buttonLabel = "✓ Claimed today";
  else if (completed) buttonLabel = loading ? "Claiming…" : `Claim +${challenge.reward} BC`;

  return (
    <section className="cb-card p-4" data-testid="daily-challenge-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-pixel text-[10px] text-[#f7931a] text-glow-orange flex items-center gap-2">
          <Target size={12} /> ▼ DAILY CHALLENGE
        </h3>
        <span className="font-pixel text-[8px] text-[#8b95a9]">
          {challenge.reward} BC
        </span>
      </div>

      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgba(247,147,26,0.12)] border border-[rgba(247,147,26,0.3)]">
          {claimed ? <Check size={18} className="text-[#2ecc71]" /> : <Gift size={18} className="text-[#f7931a]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[13px] font-semibold text-white"
            data-testid="daily-challenge-title"
          >
            {challenge.title}
          </div>
          <div className="text-[11px] text-[#8b95a9] leading-tight">
            {challenge.desc}
          </div>
        </div>
      </div>

      <div className="h-2 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden mb-3">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: claimed
              ? "#2ecc71"
              : completed
                ? "linear-gradient(90deg,#2ecc71,#f7931a)"
                : "#2ecc71",
            boxShadow: completed && !claimed ? "0 0 12px rgba(247,147,26,0.6)" : "none",
          }}
          data-testid="daily-challenge-progress-bar"
        />
      </div>

      <button
        type="button"
        onClick={claim}
        disabled={!completed || claimed || loading}
        data-testid="daily-challenge-claim-btn"
        className={`w-full font-pixel text-[10px] py-3 rounded-md transition-all ${
          claimed
            ? "bg-[rgba(46,204,113,0.1)] text-[#2ecc71] border border-[rgba(46,204,113,0.3)] cursor-default"
            : completed
              ? "bg-[#f7931a] text-black hover:bg-[#ffaa33] cursor-pointer"
              : "bg-[rgba(255,255,255,0.04)] text-[#8b95a9] border border-[rgba(255,255,255,0.08)] cursor-default"
        }`}
      >
        {buttonLabel}
      </button>
    </section>
  );
}
